import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import parse from "html-react-parser";
import { formatIndianCurrency, textDateFormatter } from "../../utils/utils";

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "Helvetica",
    paddingBottom: 80, // Increase bottom padding
    position: "relative", // For absolute positioning of footer
  },
  sectionGreen: {
    backgroundColor: "#e6f7ef",
    padding: 20, // Fix padding syntax
    borderRadius: 10, // Fix borderRadius syntax,
  },
  sectionPink: {
    backgroundColor: "#f7e6e6",
    padding: 20,
    borderRadius: 20,
  },
  section: {
    marginBottom: 10,
  },
  mainHeading: {
    fontSize: 18,
    textAlign: "center",
  },
  mainHeading2: {
    textAlign: "center",
  },
  heading: {
    fontSize: 18,
    color: "#016937",
    padding: "10px 0px",
    fontWeight: "700",
    textTransform: "uppercase",
    textAlign: "left",
    borderBottom: "3px solid #016937",
    marginBottom: 10,
  },
  subheading: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 8,
  },
  text: {
    fontSize: 12,
    marginBottom: 5,
    lineHeight: 1.4, // Added for better text spacing
  },
  headerRow: {
    backgroundColor: "#f0f0f0",
    fontWeight: "bold",
  },
  logo: {
    width: 50,
    marginBottom: 20,
  },
  destinationWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 10,
  },
  destItem: {
    width: "30%",
    marginRight: "3%",
    marginBottom: 10,
  },
  destImage: {
    width: "100%",
    height: 80,
  },
  dayBox: {
    flexDirection: "row",
    marginBottom: 5,
  },
  itineraryContent: {
    marginBottom: 15,
    breakInside: "avoid",
    minHeight: 150, 
  },
  legalContent: {
    marginBottom: 15,
    fontSize: 10,
    lineHeight: 1.4, // Added for better text spacing
  },
  contactInfo: {
    marginTop: 15,
    breakInside: "avoid", // Keep contact info together
  },
  notice: {
    fontSize: 10,
    fontStyle: "italic",
    backgroundColor: "#f7e6e6",
    padding: "20 20",
    borderRadius: "10px",
    marginTop: 20,
  },
  categoryHead: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 8,
    textTransform: "capitalize",
    backgroundColor: "#016937",
    color: "#fff",
    padding: 5,
  },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderColor: "#016937",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginBottom: 10,
    breakInside: "avoid", // Prevent table breaking across pages
  },
  tableRow: {
    flexDirection: "row",
  },
  tableCol: {
    width: "25%",
    borderStyle: "solid",
    borderColor: "#016937",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCell: {
    padding: 5,
    fontSize: 10,
    lineHeight: 1.3, // Added for better table text spacing
  },
  headerRow: {
    backgroundColor: "#016937",
    fontWeight: "bold",
    color: "#fff",
  },
  contentRow: {
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  py5: {
    paddingVertical: 5,
  },
});
// HTML Renderer Component
const HtmlRenderer = ({ html }) => {
  if (!html) return null;

  const cleanText = html
    .replace(/<[^>]*>/g, "") // Remove HTML tags
    .replace(/&nbsp;/g, " ") // Replace &nbsp; with space
    .replace(/\s+/g, " ") // Collapse multiple spaces
    .trim();

  return <Text>{cleanText}</Text>;
};

// Create Document Component
const PackagePDF = ({
  pkgDetail,
  imageData,
  logo,
  userDetails,
  userType,
  selectedCategory,
  allPackageHotels,
  calculatePriceWithMarkup,
  selectedOption,
  selectedMealPlan = "ep",
}) => {
  // Helper functions

  // Group hotels by category

  const groupHotelsByCategory = (hotels) => {
    return (
      hotels &&
      hotels.length > 0 &&
      hotels?.reduce((acc, hotel) => {
        if (!acc[hotel.hotelcategory]) {
          acc[hotel.hotelcategory] = [];
        }
        acc[hotel.hotelcategory].push(hotel);
        return acc;
      }, {})
    );
  };

  const groupedHotels = groupHotelsByCategory(allPackageHotels);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Logo */}
        <View style={styles.section}>
          <Image style={styles.logo} src={imageData || logo} />
        </View>

        {/* Package Details */}
        <View  style={{ ...styles.section, ...styles.sectionGreen }}>
          <Text style={styles.mainHeading}>{pkgDetail?.packageName}</Text>
          <Text
            style={{ ...styles.text, textAlign: "center", marginTop: "10px" }}
          >
            Validity: {textDateFormatter(pkgDetail.validFrom)} / {"  "}
            {textDateFormatter(pkgDetail.validTo)}
          </Text>
          <Text  style={styles.mainHeading}>
            Duration: {pkgDetail?.totalNights} Nights / {pkgDetail?.totalDays}{" "}
            Days
          </Text>
        </View>

        {/* Destinations */}
        <View style={styles.section}>
          <Text style={styles.heading}>Destination</Text>
          <View style={styles.destinationWrap}>
            {pkgDetail?.packageDestination?.map((item, index) => (
              <View key={index} style={styles.destItem}>
                <Image style={styles.destImage} src={item?.image} />
                <Text style={styles.text}>{item?.name}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Itinerary */}
        <View style={styles.section}>
          <Text style={styles.heading}>Day Wise Details</Text>
          {pkgDetail?.packageItinerary?.map((item, index) => (
            <View
              // break
              key={index}
              style={{ ...styles.itineraryContent, ...styles.sectionGreen }}
            >
              <View style={{ ...styles.dayBox }}>
                <Text>Day </Text>
                <Text style={styles.dayNumber}>{item?.day}</Text>
              </View>
              <Text style={styles.text}>
                <HtmlRenderer
                  html={item?.additionalDetails?.short_description}
                />
              </Text>

              <Text style={styles.text}>
                <Text style={{ fontWeight: "bold" }}>Starts: </Text>
                {item.startDestination}
              </Text>
              <Text style={styles.text}>
                <Text style={{ fontWeight: "bold" }}>End: </Text>
                {item.endDestination}
              </Text>
            </View>
          ))}
        </View>

        {/* Hotels */}

        <View style={styles.section}>
          <Text style={styles.heading}>Proposed Hotel</Text>
          {/* {selectedOption === "selectedHotels" ? ( */}
          {true ? (
            <>
              {Object.entries(groupedHotels).map(
                ([category, hotelsselected]) => {
                  if (selectedCategory && category !== selectedCategory) {
                    return null;
                  }

                  const groupByCategory = pkgDetail.packageRates.filter(
                    (item) => item.accommodationType === category
                  );

                  return (
                    <View break key={category} style={{ marginBottom: 20 }}>
                      <Text style={styles.categoryHead}>{category}</Text>

                      {/* Hotels Table */}
                      <View style={styles.table}>
                        <View style={[styles.tableRow, styles.headerRow]}>
                          <View style={[styles.tableCol, { width: "50%" }]}>
                            <Text style={styles.tableCell}>Destination</Text>
                          </View>
                          <View style={[styles.tableCol, { width: "50%" }]}>
                            <Text style={styles.tableCell}>Hotel</Text>
                          </View>
                        </View>

                        {hotelsselected?.map((hotel, index) => (
                          <View
                            key={index}
                            style={[styles.tableRow, styles.contentRow]}
                          >
                            <View style={[styles.tableCol, { width: "50%" }]}>
                              <Text style={[styles.tableCell, styles.py5]}>
                                {hotel?.city}
                              </Text>
                            </View>
                            <View style={[styles.tableCol, { width: "50%" }]}>
                              <Text style={[styles.tableCell, styles.py5]}>
                                {hotel?.name}
                              </Text>
                            </View>
                          </View>
                        ))}
                      </View>

                      <View style={{ marginTop: 10 }}>
                        <Text style={{ ...styles.contentRow }}>
                          Rates of {category} Category
                        </Text>

                        {/* EP Rates */}
                        {selectedMealPlan === "ep" && groupByCategory.filter(
                          (item) => item.mealPlan === "ep"
                        ).length > 0 && (
                          <View style={{ marginTop: 5 }}>
                            <Text style={{ ...styles.contentRow }}>EP</Text>
                            <View style={styles.table}>
                              <View style={[styles.tableRow, styles.headerRow]}>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>PAX</Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>Price</Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>
                                    Extra Adult
                                  </Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>
                                    Child WB / WOB
                                  </Text>
                                </View>
                              </View>

                              {groupByCategory
                                .filter(
                                  (item) =>
                                    item.mealPlan === "ep" &&
                                    item.accommodationType === category
                                )
                                .map((hotel, index) => (
                                  <View
                                    key={index}
                                    style={[styles.tableRow, styles.contentRow]}
                                  >
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {hotel?.pax} PAX
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.price
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.extraAdult || "N/A"
                                        )}
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.childWBed || "N/A"
                                        )}{" "}
                                        /{" "}
                                        {calculatePriceWithMarkup(
                                          hotel?.childWoBed
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                  </View>
                                ))}
                            </View>
                          </View>
                        )}

                        {/* CP Rates */}
                        {selectedMealPlan === "cp" && groupByCategory.filter(
                          (item) => item.mealPlan === "cp"
                        ).length > 0 && (
                          <View style={{ marginTop: 5 }}>
                            <Text style={{ ...styles.contentRow }}>CP</Text>
                            <View style={styles.table}>
                              <View style={[styles.tableRow, styles.headerRow]}>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>PAX</Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>Price</Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>
                                    Extra Adult
                                  </Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>
                                    Child WB / WOB
                                  </Text>
                                </View>
                              </View>

                              {groupByCategory
                                .filter((item) => item.mealPlan === "cp")
                                .map((hotel, index) => (
                                  <View
                                    key={index}
                                    style={[styles.tableRow, styles.contentRow]}
                                  >
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {hotel?.pax} PAX
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.price
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.extraAdult
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.childWBed
                                        ) || "N/A"}{" "}
                                        /{" "}
                                        {calculatePriceWithMarkup(
                                          hotel?.childWoBed
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                  </View>
                                ))}
                            </View>
                          </View>
                        )}

                        {/* MAP Rates */}
                        {selectedMealPlan === "map" && groupByCategory.filter(
                          (item) => item.mealPlan === "map"
                        ).length > 0 && (
                          <View style={{ marginTop: 5 }}>
                            <Text style={{ ...styles.contentRow }}>MAP</Text>
                            <View style={styles.table}>
                              <View style={[styles.tableRow, styles.headerRow]}>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>PAX</Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>Price</Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>
                                    Extra Adult
                                  </Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>
                                    Child WB / WOB
                                  </Text>
                                </View>
                              </View>

                              {groupByCategory
                                .filter((item) => item.mealPlan === "map")
                                .map((hotel, index) => (
                                  <View
                                    key={index}
                                    style={[styles.tableRow, styles.contentRow]}
                                  >
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {hotel?.pax} PAX
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.price
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.extraAdult || "N/A"
                                        )}
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.childWBed || "N/A"
                                        )}{" "}
                                        /{" "}
                                        {calculatePriceWithMarkup(
                                          hotel?.childWoBed
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                  </View>
                                ))}
                            </View>
                          </View>
                        )}

                        {/* AP Rates */}
                        {selectedMealPlan === "ap" && groupByCategory.filter(
                          (item) => item.mealPlan === "ap"
                        ).length > 0 && (
                          <View style={{ marginTop: 5 }}>
                            <Text style={{ ...styles.contentRow }}>AP</Text>
                            <View style={styles.table}>
                              <View style={[styles.tableRow, styles.headerRow]}>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>PAX</Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>Price</Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>
                                    Extra Adult
                                  </Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>
                                    Child WB / WOB
                                  </Text>
                                </View>
                              </View>

                              {groupByCategory
                                .filter((item) => item.mealPlan === "ap")
                                .map((hotel, index) => (
                                  <View
                                    key={index}
                                    style={[styles.tableRow, styles.contentRow]}
                                  >
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {hotel?.pax} PAX
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.price
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.extraAdult
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.childWBed
                                        ) || "N/A"}{" "}
                                        /{" "}
                                        {calculatePriceWithMarkup(
                                          hotel?.childWoBed
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                  </View>
                                ))}
                            </View>
                          </View>
                        )}
                      </View>
                    </View>
                  );
                }
              )}
            </>
          ) : (
            <>
              {Object.entries(groupedHotels).map(
                ([category, hotelsselected]) => {
                  const groupByCategory = pkgDetail.packageRates.filter(
                    (item) => item.accommodationType === category
                  );

                  return (
                    <View break key={category} style={{ marginBottom: 20 }}>
                      <Text style={styles.categoryHead}>{category}</Text>

                      {/* Hotels Table */}
                      <View style={styles.table}>
                        <View style={[styles.tableRow, styles.headerRow]}>
                          <View style={[styles.tableCol, { width: "50%" }]}>
                            <Text style={styles.tableCell}>Destination</Text>
                          </View>
                          <View style={[styles.tableCol, { width: "50%" }]}>
                            <Text style={styles.tableCell}>Hotel</Text>
                          </View>
                        </View>

                        {hotelsselected?.map((hotel, index) => (
                          <View
                            key={index}
                            style={[styles.tableRow, styles.contentRow]}
                          >
                            <View style={[styles.tableCol, { width: "50%" }]}>
                              <Text style={[styles.tableCell, styles.py5]}>
                                {hotel?.city}
                              </Text>
                            </View>
                            <View style={[styles.tableCol, { width: "50%" }]}>
                              <Text style={[styles.tableCell, styles.py5]}>
                                {hotel?.name}
                              </Text>
                            </View>
                          </View>
                        ))}
                      </View>

                      <View style={{ marginTop: 10 }}>
                        <Text style={{ ...styles.contentRow }}>
                          Rates of {category} Category
                        </Text>

                        {/* EP Rates */}
                        {selectedMealPlan === "ep" && groupByCategory.filter(
                          (item) => item.mealPlan === "ep"
                        ).length > 0 && (
                          <View style={{ marginTop: 5 }}>
                            <Text style={{ ...styles.contentRow }}>EP</Text>
                            <View style={styles.table}>
                              <View style={[styles.tableRow, styles.headerRow]}>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>PAX</Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>Price</Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>
                                    Extra Adult
                                  </Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>
                                    Child WB / WOB
                                  </Text>
                                </View>
                              </View>

                              {groupByCategory
                                .filter(
                                  (item) =>
                                    item.mealPlan === "ep" &&
                                    item.accommodationType === category
                                )
                                .map((hotel, index) => (
                                  <View
                                    key={index}
                                    style={[styles.tableRow, styles.contentRow]}
                                  >
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {hotel?.pax} PAX
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.price
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.extraAdult || "N/A"
                                        )}
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.childWBed || "N/A"
                                        )}{" "}
                                        /{" "}
                                        {calculatePriceWithMarkup(
                                          hotel?.childWoBed
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                  </View>
                                ))}
                            </View>
                          </View>
                        )}

                        {/* CP Rates */}
                        {selectedMealPlan === "cp" && groupByCategory.filter(
                          (item) => item.mealPlan === "cp"
                        ).length > 0 && (
                          <View style={{ marginTop: 5 }}>
                            <Text style={{ ...styles.contentRow }}>CP</Text>
                            <View style={styles.table}>
                              <View style={[styles.tableRow, styles.headerRow]}>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>PAX</Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>Price</Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>
                                    Extra Adult
                                  </Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>
                                    Child WB / WOB
                                  </Text>
                                </View>
                              </View>

                              {groupByCategory
                                .filter((item) => item.mealPlan === "cp")
                                .map((hotel, index) => (
                                  <View
                                    key={index}
                                    style={[styles.tableRow, styles.contentRow]}
                                  >
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {hotel?.pax} PAX
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.price
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.extraAdult
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.childWBed
                                        ) || "N/A"}{" "}
                                        /{" "}
                                        {calculatePriceWithMarkup(
                                          hotel?.childWoBed
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                  </View>
                                ))}
                            </View>
                          </View>
                        )}

                        {/* MAP Rates */}
                        {selectedMealPlan === "map" && groupByCategory.filter(
                          (item) => item.mealPlan === "map"
                        ).length > 0 && (
                          <View style={{ marginTop: 5 }}>
                            <Text style={{ ...styles.contentRow }}>MAP</Text>
                            <View style={styles.table}>
                              <View style={[styles.tableRow, styles.headerRow]}>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>PAX</Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>Price</Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>
                                    Extra Adult
                                  </Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>
                                    Child WB / WOB
                                  </Text>
                                </View>
                              </View>

                              {groupByCategory
                                .filter((item) => item.mealPlan === "map")
                                .map((hotel, index) => (
                                  <View
                                    key={index}
                                    style={[styles.tableRow, styles.contentRow]}
                                  >
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {hotel?.pax} PAX
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.price
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.extraAdult || "N/A"
                                        )}
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.childWBed || "N/A"
                                        )}{" "}
                                        /{" "}
                                        {calculatePriceWithMarkup(
                                          hotel?.childWoBed
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                  </View>
                                ))}
                            </View>
                          </View>
                        )}

                        {/* AP Rates */}
                         {selectedMealPlan === "ap" && groupByCategory.filter(
                          (item) => item.mealPlan === "ap"
                        ).length > 0 && (
                          <View style={{ marginTop: 5 }}>
                            <Text style={{ ...styles.contentRow }}>AP</Text>
                            <View style={styles.table}>
                              <View style={[styles.tableRow, styles.headerRow]}>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>PAX</Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>Price</Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>
                                    Extra Adult
                                  </Text>
                                </View>
                                <View style={styles.tableCol}>
                                  <Text style={styles.tableCell}>
                                    Child WB / WOB
                                  </Text>
                                </View>
                              </View>

                              {groupByCategory
                                .filter((item) => item.mealPlan === "ap")
                                .map((hotel, index) => (
                                  <View
                                    key={index}
                                    style={[styles.tableRow, styles.contentRow]}
                                  >
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {hotel?.pax} PAX
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.price
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.extraAdult || "N/A"
                                        )}
                                      </Text>
                                    </View>
                                    <View style={styles.tableCol}>
                                      <Text
                                        style={[styles.tableCell, styles.py5]}
                                      >
                                        {calculatePriceWithMarkup(
                                          hotel?.childWBed || "N/A"
                                        )}{" "}
                                        /{" "}
                                        {calculatePriceWithMarkup(
                                          hotel?.childWoBed
                                        ) || "N/A"}
                                      </Text>
                                    </View>
                                  </View>
                                ))}
                            </View>
                          </View>
                        )}
                      </View>
                    </View>
                  );
                }
              )}
            </>
          )}
        </View>

        {/* Inclusions */}
        {pkgDetail?.packagePolicies?.filter((item) => item.type === "inclusion")
          .length > 0 && (
          <View style={styles.section}>
            <Text style={styles.heading}>Inclusions</Text>
            <View style={styles.legalContent}>
              {pkgDetail.packagePolicies
                .filter((item) => item.type === "inclusion")
                .map((item, index) => (
                  <View key={index}>
                    {item.content
                      .replace(/&nbsp;/g, " ") // Remove non-breaking spaces
                      .replace(/<\/p>/g, "\n") // Replace closing p tags with new lines
                      .replace(/<p>/g, "• ") // Replace opening p tags with bullet points
                      .split("\n")
                      .map((line, i) => (
                        <Text key={i} style={styles.bulletText}>
                          {line.trim()}
                        </Text>
                      ))}
                  </View>
                ))}
            </View>
          </View>
        )}

        {/* Exclusions */}

        {pkgDetail?.packagePolicies?.filter((item) => item.type === "exclusion")
          .length > 0 && (
          <View style={styles.section}>
            <Text style={styles.heading}>Exclusions</Text>
            <View style={styles.legalContent}>
              {pkgDetail.packagePolicies
                .filter((item) => item.type === "exclusion")
                .map((item, index) => (
                  <View key={index}>
                    {item.content
                      .replace(/&nbsp;/g, " ") // Remove non-breaking spaces
                      .replace(/<\/p>/g, "\n") // Replace closing p tags with new lines
                      .replace(/<p>/g, "• ") // Replace opening p tags with bullet points
                      .split("\n")
                      .map((line, i) => (
                        <Text key={i} style={styles.bulletText}>
                          {line.trim()}
                        </Text>
                      ))}
                  </View>
                ))}
            </View>
          </View>
        )}

        {/* Terms and Conditions */}

        {pkgDetail?.packagePolicies?.filter(
          (item) => item.type === "terms_and_conditions"
        ).length > 0 && (
          <View style={styles.section}>
            <Text style={styles.heading}>Terms and Conditions</Text>
            <View style={styles.legalContent}>
              {pkgDetail.packagePolicies
                .filter((item) => item.type === "terms_and_conditions")
                .map((item, index) => (
                  <View key={index}>
                    {item.content
                      .replace(/&nbsp;/g, " ") // Remove non-breaking spaces
                      .replace(/<\/p>/g, "\n") // Replace closing p tags with new lines
                      .replace(/<p>/g, "• ") // Replace opening p tags with bullet points
                      .split("\n")
                      .map((line, i) => (
                        <Text key={i} style={styles.bulletText}>
                          {line.trim()}
                        </Text>
                      ))}
                  </View>
                ))}
            </View>
          </View>
        )}

        {/* Cancellation Policy */}

        {pkgDetail?.packagePolicies?.filter(
          (item) => item.type === "cancellation_policy"
        ).length > 0 && (
          <View style={styles.section}>
            <Text style={styles.heading}>Cancellation Policy</Text>
            <View style={styles.legalContent}>
              {pkgDetail.packagePolicies
                .filter((item) => item.type === "cancellation_policy")
                .map((item, index) => (
                  <View key={index}>
                    {item.content
                      .replace(/&nbsp;/g, " ") // Remove non-breaking spaces
                      .replace(/<\/p>/g, "\n") // Replace closing p tags with new lines
                      .replace(/<p>/g, "• ") // Replace opening p tags with bullet points
                      .split("\n")
                      .map((line, i) => (
                        <Text key={i} style={styles.bulletText}>
                          {line.trim()}
                        </Text>
                      ))}
                  </View>
                ))}
            </View>
          </View>
        )}

        {/* Contact Info */}
        <View style={styles.contactInfo}>
          <Text style={styles.heading}>Get in touch</Text>

          <View style={styles.sectionGreen}>
            <Text style={styles.text}>
              Email:{" "}
              {userType === "agent" && userDetails?.email
                ? userDetails.email
                : "info@mountainmysteries.com"}
            </Text>
            <Text style={styles.text}>
              Phone No. :{" "}
              {userType === "agent" && userDetails?.phone
                ? userDetails.phone
                : "+91 98160-12966"}
            </Text>
          </View>
          
        </View>

        {/* Notice */}
        <View style={{ ...styles.notice, ...styles.sectionPink }}>
          <Text>
            This is one of our most popular holiday package to your chosen
            destination. The pricing is indicative and can change depending on
            hotels etc. chosen. Once your Travel Expert gets in touch with you,
            feel free to ask for any customisation in this itinerary or have a
            fresh itinerary designed, as per your preference. When you are
            making the booking, please do check that all the selected package
            inclusions are showing on the package review page and confirm that
            all elements including transportation, hotels and sightseeing are
            exactly as finalised by you.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default PackagePDF;
